import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
import * as firstPencilActionCreators from '@makemydeal/dr-dash-store/actions/firstPencilActionCreators';
import * as firstPencilSelectors from '@makemydeal/dr-dash-store/selectors/firstPencilSelectors';
import { Alert } from '@interstate/components/Alert';
import { Typography } from '@interstate/components/Typography';

import { IncentivesTableView } from '../Programs/IncentivesTableView';
import type { IncentiveDisabledReason } from '../Programs/IncentivesTableView';
import { NO_DATA_EXIST_MESSAGE, ZERO_AMOUNT_MESSAGE, buildExclusionMessage } from '../../constants';
import { findBlockingIncentive, toggleIncentiveSelection } from './utils';
import type { FirstPencilIncentiveRow } from './types';

type FirstPencilIncentivesTableProps = {
    incentives: FirstPencilIncentiveRow[];
};

export const FirstPencilIncentivesTable: FC<FirstPencilIncentivesTableProps> = ({ incentives }) => {
    const dispatch = useDispatch();
    const isLocked = dealLockHooks.useIsDealLocked();
    const isProgramIncentivesRestorationEnabled = useSelector(featureToggleSelectors.isProgramIncentivesRestorationEnabled);
    const isProgramsLoading = useSelector(firstPencilSelectors.isProgramsLoading);
    const [deselectedIds, setDeselectedIds] = useState<number[]>([]);

    const handleCheckboxChange = useCallback(
        (incentive: FirstPencilIncentiveRow, checked: boolean) => {
            if (!checked) {
                setDeselectedIds((prev) => (prev.includes(incentive.incentiveId) ? prev : [...prev, incentive.incentiveId]));
            }
            dispatch(firstPencilActionCreators.incentivesSelected(toggleIncentiveSelection(incentives, incentive, checked)));
        },
        [dispatch, incentives]
    );

    const getDisabledReason = useCallback(
        (incentive: FirstPencilIncentiveRow): IncentiveDisabledReason | undefined => {
            const blocker = findBlockingIncentive(incentive, incentives);
            if (blocker) {
                return { kind: 'exclusion', message: buildExclusionMessage(blocker.programName) };
            }
            const blockedByZeroAmount =
                isProgramIncentivesRestorationEnabled &&
                incentive.cash === 0 &&
                !incentive.selected &&
                !deselectedIds.includes(incentive.incentiveId);
            return blockedByZeroAmount ? { kind: 'zero-amount', message: ZERO_AMOUNT_MESSAGE } : undefined;
        },
        [incentives, isProgramIncentivesRestorationEnabled, deselectedIds]
    );

    const isCheckboxDisabled = useCallback(
        (incentive: FirstPencilIncentiveRow, disabledReason: IncentiveDisabledReason | undefined) =>
            Boolean(disabledReason) ||
            isLocked ||
            isProgramsLoading ||
            (!isProgramIncentivesRestorationEnabled && !incentive.isConditional),
        [isLocked, isProgramsLoading, isProgramIncentivesRestorationEnabled]
    );

    if (incentives.length === 0) {
        return (
            <Alert type="info" data-testid="first-pencil-incentives-empty">
                <Typography variant="label-md">{NO_DATA_EXIST_MESSAGE}</Typography>
            </Alert>
        );
    }

    return (
        <IncentivesTableView
            rows={incentives}
            getDisabledReason={getDisabledReason}
            isCheckboxDisabled={isCheckboxDisabled}
            onToggle={handleCheckboxChange}
            testIdPrefix="first-pencil-"
        />
    );
};
